import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiSelectOption } from '../select/select.component';

let uiMultiselectUid = 0;

/**
 * Átomo: select de selección múltiple con chips, búsqueda interna,
 * "Seleccionar todo / Limpiar todo" y badge contador.
 * El panel NO se cierra al seleccionar — solo con click fuera o Escape.
 * Usa position:fixed para escapar overflow:hidden de ancestros.
 */
@Component({
  selector: 'ui-multiselect',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  templateUrl: './multiselect.component.html',
  styleUrl: './multiselect.component.scss',
  host: {
    class: 'ui-multiselect-host',
    '[class.ui-multiselect-host--block]': 'block()',
    '[class.ui-multiselect-host--fit-panel]': 'fitPanelToContent()',
  },
})
export class UiMultiselectComponent {
  @ViewChild('triggerBtn') private triggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  readonly label = input('');
  readonly options = input<UiSelectOption[]>([]);
  readonly selected = model<UiSelectOption[]>([]);
  readonly placeholder = input('Selecciona opciones');
  readonly maxChipsVisible = input(3);

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);
  readonly searchPlaceholder = input('Buscar...');

  readonly showLabel = input(true);
  readonly block = input(false);
  /**
   * Si es true, el panel usa el ancho del contenido (mínimo el del trigger)
   * en lugar de igualar el ancho del trigger. Útil en columnas angostas.
   */
  readonly fitPanelToContent = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly selectedChange = output<UiSelectOption[]>();
  readonly opened = output<void>();
  readonly closed = output<void>();

  protected readonly generatedId = `ui-multiselect-${++uiMultiselectUid}`;
  protected readonly triggerId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly listboxId = computed(() => `${this.triggerId()}-listbox`);
  protected readonly messageId = computed(() => `${this.triggerId()}-message`);

  protected readonly isOpen = signal(false);
  protected readonly searchQuery = signal('');

  protected readonly panelRect = signal<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly ariaDescribedBy = computed(() =>
    this.message() ? this.messageId() : null,
  );

  protected readonly filteredOptions = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return this.options();
    return this.options().filter((o) => o.label.toLowerCase().includes(q));
  });

  /** Índice de la opción activa durante la navegación por teclado (-1 = ninguna). */
  protected readonly activeIndex = signal(-1);

  /** ID DOM de la opción actualmente activa (para aria-activedescendant). */
  protected readonly activeOptionId = computed(() => {
    const idx = this.activeIndex();
    if (idx < 0) return null;
    return `${this.listboxId()}-opt-${idx}`;
  });

  /** Devuelve el ID DOM de la opción en la posición dada. */
  protected optionId(index: number): string {
    return `${this.listboxId()}-opt-${index}`;
  }

  /** Chips visibles en el trigger (respeta maxChipsVisible). */
  protected readonly visibleChips = computed(() =>
    this.selected().slice(0, this.maxChipsVisible()),
  );

  /** Cantidad de selecciones que no se muestran como chip. */
  protected readonly hiddenCount = computed(() =>
    Math.max(0, this.selected().length - this.maxChipsVisible()),
  );

  /** true si todas las opciones no-disabled están seleccionadas. */
  protected readonly allSelected = computed(() => {
    const active = this.options().filter((o) => !o.disabled);
    return active.length > 0 && active.every((o) => this.isSelected(o));
  });

  constructor(private readonly elRef: ElementRef<HTMLElement>) {
    const onScroll = (event: Event) => {
      if (!this.isOpen()) return;
      const panel = document.getElementById(this.listboxId());
      if (panel?.contains(event.target as Node)) return;
      this.close();
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('scroll', onScroll, { capture: true }),
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const panel = document.getElementById(this.listboxId());
    const insideHost = this.elRef.nativeElement.contains(target);
    const insidePanel = panel?.contains(target) ?? false;
    if (!insideHost && !insidePanel) {
      this.close();
    }
  }

  /** Cierra con Escape y devuelve el foco al trigger. */
  @HostListener('keydown.escape')
  onEscape(): void {
    this.close(true);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isOpen()) this.close(false);
  }

  protected toggle(): void {
    if (this.disabled()) return;
    this.isOpen() ? this.close(true) : this.open();
  }

  protected open(): void {
    const rect = this.triggerBtn.nativeElement.getBoundingClientRect();
    this.panelRect.set({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    this.isOpen.set(true);
    this.searchQuery.set('');
    this.activeIndex.set(-1);
    this.opened.emit();
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  /**
   * @param refocus – true para devolver el foco al trigger (Esc).
   *                  false cuando el cierre es por Tab o resize.
   */
  protected close(refocus = false): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.panelRect.set(null);
    this.activeIndex.set(-1);
    this.closed.emit();
    if (refocus) {
      setTimeout(() => this.triggerBtn?.nativeElement?.focus(), 0);
    }
  }

  /** Cierra el panel desde fuera del componente (p.ej. exclusión mutua en toolbar). */
  closePanel(): void { this.close(false); }

  protected toggleOption(option: UiSelectOption): void {
    if (option.disabled) return;
    const current = this.selected();
    const next = this.isSelected(option)
      ? current.filter((o) => o.id !== option.id)
      : [...current, option];
    this.selected.set(next);
    this.selectedChange.emit(next);
  }

  protected removeChip(option: UiSelectOption, event: Event): void {
    event.stopPropagation();
    const next = this.selected().filter((o) => o.id !== option.id);
    this.selected.set(next);
    this.selectedChange.emit(next);
  }

  protected toggleAll(): void {
    if (this.allSelected()) {
      this.selected.set([]);
      this.selectedChange.emit([]);
    } else {
      const next = this.options().filter((o) => !o.disabled);
      this.selected.set(next);
      this.selectedChange.emit(next);
    }
  }

  protected clearAll(event: Event): void {
    event.stopPropagation();
    this.selected.set([]);
    this.selectedChange.emit([]);
  }

  protected onSearch(query: string): void {
    this.searchQuery.set(query);
    this.activeIndex.set(-1);
  }

  protected isSelected(option: UiSelectOption): boolean {
    return this.selected().some((o) => o.id === option.id);
  }

  // ── Teclado ───────────────────────────────────────────────────────────────

  /** Manejador de teclas en el botón trigger. */
  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
        }
        break;
      case 'Enter':
      case ' ':
        if (!this.isOpen()) {
          event.preventDefault();
          this.open();
        }
        break;
      case 'Tab':
        if (this.isOpen()) this.close(false);
        break;
    }
  }

  /** Manejador de teclas en el input de búsqueda. */
  protected onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveIndex(-1);
        break;
      case 'Enter': {
        event.preventDefault();
        const idx = this.activeIndex();
        const opt = this.filteredOptions()[idx];
        if (opt) this.toggleOption(opt);
        break;
      }
      case 'Tab':
        this.close(false);
        break;
    }
  }

  /** Mueve el índice activo en `delta` pasos, saltando opciones deshabilitadas. */
  private moveActiveIndex(delta: number): void {
    const opts = this.filteredOptions();
    if (!opts.length) return;
    let next = this.activeIndex() + delta;
    let attempts = 0;
    while (next >= 0 && next < opts.length && opts[next].disabled && attempts < opts.length) {
      next += delta;
      attempts++;
    }
    next = Math.max(0, Math.min(next, opts.length - 1));
    if (opts[next]?.disabled) return;
    this.activeIndex.set(next);
    setTimeout(() => {
      document.getElementById(this.optionId(next))?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }
}
